import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { RecordAccessGrant } from '@econtractor/types';
import { UserAvatarStack, UserNameItem } from 'modules/user';
import SolarIcon from 'components/icons/SolarIcon';

interface AccessUserListProps {
    title: string;
    icon: string;
    rows: RecordAccessGrant[];
    canManage: boolean;
    busy: boolean;
    onRemove: (userId: number) => void;
}

const AccessUserList = ({
    title,
    icon,
    rows,
    canManage,
    busy,
    onRemove,
}: AccessUserListProps) => {
    const { t } = useTranslation(['common']);
    const [expanded, setExpanded] = useState(false);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <h6 className="access-grant-section__title mb-0">
                    <SolarIcon name={icon} />
                    {title}
                </h6>
                {rows.length > 0 ? (
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={() => setExpanded((value) => !value)}
                    >
                        {expanded ? t('buttons.less') : t('buttons.more')}
                    </button>
                ) : null}
            </div>
            {!rows.length ? (
                <div className="text-muted">{t('Users.accessEmpty')}</div>
            ) : expanded ? (
                <ul className="access-grant-list list-unstyled mb-0">
                    {rows.map((grant) => (
                        <li key={grant.ID} className="access-grant-list__row d-flex align-items-center gap-2">
                            <div className="flex-grow-1 min-w-0">
                                <UserNameItem
                                    smallMode
                                    user={{
                                        ID: grant.ID,
                                        userName: grant.userName,
                                        userEmail: grant.userEmail,
                                        userPicture: grant.userPicture,
                                    }}
                                />
                            </div>
                            {canManage ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="link"
                                    className="text-danger p-1"
                                    disabled={busy}
                                    title={t('buttons.delete')}
                                    onClick={() => onRemove(grant.ID)}
                                >
                                    <SolarIcon name="x-lg" />
                                </Button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : (
                <UserAvatarStack
                    items={rows.map((grant) => ({
                        id: grant.ID,
                        name: grant.userName,
                        picture: grant.userPicture,
                        hint: grant.userName,
                    }))}
                />
            )}
        </div>
    );
};

export default AccessUserList;
